import React, { useState, useMemo } from 'react';
import { Search, RefreshCw, Loader2 } from 'lucide-react';

const OpenOrdersTable = ({ orders, isLoading, error, onRefresh, updating }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [sideFilter, setSideFilter] = useState("all");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Helper function to safely format price
  const formatPrice = (price) => {
    if (price === null || price === undefined) {
      return '0.00';
    }
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice)) {
      return '0.00';
    }
    return numPrice.toFixed(2);
  };

  // Helper function to categorize orders by status
  const categorizeOrders = (orders) => {
    const openStatuses = ['TRANSIT', 'PENDING', 'OPEN'];
    const executedStatuses = ['COMPLETE', 'EXECUTED', 'TRADED'];
    const rejectedStatuses = ['CANCELLED', 'REJECTED'];

    return {
      all: orders,
      open: orders.filter(order => openStatuses.some(status => 
        order.order_status.toLowerCase().includes(status.toLowerCase())
      )),
      executed: orders.filter(order => executedStatuses.some(status => 
        order.order_status.toLowerCase().includes(status.toLowerCase())
      )),
      rejected: orders.filter(order => rejectedStatuses.some(status => 
        order.order_status.toLowerCase().includes(status.toLowerCase())
      ))
    };
  };

  // Get current tab's orders
  const getCurrentTabOrders = () => {
    const categorizedOrders = categorizeOrders(orders);
    return categorizedOrders[activeTab] || [];
  };

  // Group orders by symbol and side
  const groupedOrders = useMemo(() => {
    const currentTabOrders = getCurrentTabOrders();
    const grouped = new Map();

    currentTabOrders.forEach((order) => {
      const key = `${order.symbol}-${order.side}`;

      if (!grouped.has(key)) {
        grouped.set(key, {
          symbol: order.symbol,
          side: order.side,
          price: order.price,
          order_type: order.order_type,
          order_status: order.order_status,
          order_datetime: order.order_datetime,
          quantities: [],
        });
      }

      const group = grouped.get(key);

      // Find or create quantity group
      let qtyGroup = group.quantities.find((q) => q.qty === order.qty);
      if (!qtyGroup) {
        qtyGroup = {
          qty: order.qty,
          clients: [],
        };
        group.quantities.push(qtyGroup);
      }

      // Add client to quantity group
      qtyGroup.clients.push({
        client_id: order.client_id,
        name: order.name,
        order_status: order.order_status,
        message: order.message,
        order_id: order.order_id,
      });
    });

    // Sort quantities within each group
    grouped.forEach((group) => {
      group.quantities.sort((a, b) => a.qty - b.qty);
    });

    return Array.from(grouped.values());
  }, [orders, activeTab]);

  const getOrderStatusStyle = (status) => {
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case 'complete':
      case 'executed':
      case 'traded':
        return 'bg-green-900/30 text-green-400 border-green-700';
      case 'open':
      case 'active':
      case 'pending':
      case 'transit':
        return 'bg-blue-900/30 text-blue-400 border-blue-700';
      case 'cancelled':
      case 'rejected':
      case 'error':
        return 'bg-red-900/30 text-red-400 border-red-700';
      default:
        return 'bg-zinc-700 text-zinc-300 border-zinc-600';
    }
  };

  const getSideTagColor = (side) => {
    const sideUpper = side.toUpperCase();
    if (sideUpper === 'BUY' || sideUpper === 'LONG') {
      return 'bg-green-900/40 text-green-400 border-green-700';
    } else if (sideUpper === 'SELL' || sideUpper === 'SHORT') {
      return 'bg-red-900/40 text-red-400 border-red-700';
    }
    return 'bg-zinc-700 text-zinc-300 border-zinc-600';
  };

  // Filter grouped orders based on search term and side filter
  const filteredGroupedOrders = useMemo(() => {
    let filtered = groupedOrders;
    
    // Apply side filter
    if (sideFilter !== 'all') {
      filtered = filtered.filter(group => group.side.toLowerCase() === sideFilter.toLowerCase());
    }
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(group => {
        const searchMatch = 
          group.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
          group.quantities.some(qty => 
            qty.clients.some(client => 
              client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
              client.client_id.toLowerCase().includes(searchTerm.toLowerCase())
            )
          );
        return searchMatch;
      });
    }
    
    return filtered;
  }, [groupedOrders, searchTerm, sideFilter]);

  // Sort filtered orders
  const sortedFilteredOrders = useMemo(() => {
    if (!sortConfig.key) return filteredGroupedOrders;
    
    const sorted = [...filteredGroupedOrders].sort((a, b) => {
      let aValue, bValue;
      
      switch(sortConfig.key) {
        case 'symbol':
          aValue = a.symbol;
          bValue = b.symbol;
          break;
        case 'side':
          aValue = a.side;
          bValue = b.side;
          break;
        case 'price':
          aValue = parseFloat(a.price) || 0;
          bValue = parseFloat(b.price) || 0;
          break;
        case 'order_type':
          aValue = a.order_type;
          bValue = b.order_type;
          break;
        case 'order_status':
          aValue = a.order_status;
          bValue = b.order_status;
          break;
        case 'order_datetime':
          aValue = new Date(a.order_datetime);
          bValue = new Date(b.order_datetime);
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  }, [filteredGroupedOrders, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return (
        <svg className="w-3 h-3 ml-1 text-zinc-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    return sortConfig.direction === 'asc' ? (
      <svg className="w-3 h-3 ml-1 text-zinc-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-3 h-3 ml-1 text-zinc-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  // Get order counts for each tab
  const orderCounts = useMemo(() => {
    const categorized = categorizeOrders(orders);
    return {
      all: categorized.all.length,
      open: categorized.open.length,
      executed: categorized.executed.length,
      rejected: categorized.rejected.length
    };
  }, [orders]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64 bg-zinc-900">
        <Loader2 className="h-10 w-10 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-red-400 space-y-4 bg-zinc-900">
        <p className="text-center max-w-md">{error}</p>
        <button 
          onClick={onRefresh} 
          className="px-4 py-2 bg-zinc-800 text-zinc-200 rounded-md border border-zinc-700 hover:bg-zinc-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-4 bg-zinc-900 text-zinc-200">
      {/* Header Controls */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 px-4 pt-4">
        {/* Tabs */}
        <div className="flex gap-2 bg-zinc-800 p-1 rounded-lg">
          {['all', 'open', 'executed', 'rejected'].map((tab) => {
            // Custom label for 'rejected'
            const label =
              tab === 'rejected'
                ? 'Rejected/Cancelled'
                : tab.charAt(0).toUpperCase() + tab.slice(1);
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === tab
                    ? 'bg-zinc-700 text-white'
                    : 'text-zinc-400 hover:text-zinc-200'
                }`}
              >
                {label} ({orderCounts[tab]})
              </button>
            );
          })}
        </div>

        {/* Search and Refresh */}
        <div className="flex items-center gap-4 w-full md:w-auto">
          {/* Side Filter */}
          <div className="flex gap-2 bg-zinc-800 p-1 rounded-lg border border-zinc-700">
            <button
              onClick={() => setSideFilter('all')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                sideFilter === 'all' 
                  ? 'bg-zinc-700 text-white' 
                  : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setSideFilter('buy')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                sideFilter === 'buy' 
                  ? 'bg-green-900/40 text-green-400 border border-green-700' 
                  : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              Buy
            </button>
            <button
              onClick={() => setSideFilter('sell')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                sideFilter === 'sell' 
                  ? 'bg-red-900/40 text-red-400 border border-red-700' 
                  : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              Sell
            </button>
          </div>

          <div className="relative flex-1 md:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500" />
            <input 
              placeholder="Search by Symbol or Client Name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full px-4 py-2 bg-zinc-800 text-zinc-200 rounded-md border border-zinc-700 focus:border-zinc-600 focus:outline-none"
            />
          </div>

          <button 
            onClick={onRefresh}
            className="flex items-center gap-2 px-4 py-2 bg-zinc-800 text-zinc-200 rounded-md border border-zinc-700 hover:bg-zinc-700 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            {updating === true ? <Loader2 className="h-4 w-4 animate-spin" /> : <span className="hidden sm:inline">Update</span>}
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-zinc-800 border border-zinc-700 h-[calc(100vh-150px)] overflow-y-auto">
        <div className="">
          <table className="w-full relative">
            <thead className="bg-zinc-700 sticky top-0 h-[40px] z-10">
              <tr>
                <th 
                  onClick={() => handleSort('symbol')}
                  className="px-6 py-3 text-left text-xs font-medium text-zinc-300 uppercase tracking-wider cursor-pointer hover:bg-zinc-600"
                >
                  <div className="flex items-center">
                    Symbol
                    <SortIcon columnKey="symbol" />
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('side')}
                  className="px-6 py-3 text-left text-xs font-medium text-zinc-300 uppercase tracking-wider cursor-pointer hover:bg-zinc-600"
                >
                  <div className="flex items-center">
                    Side
                    <SortIcon columnKey="side" />
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('price')}
                  className="px-6 py-3 text-left text-xs font-medium text-zinc-300 uppercase tracking-wider cursor-pointer hover:bg-zinc-600"
                >
                  <div className="flex items-center">
                    Price
                    <SortIcon columnKey="price" />
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('order_type')}
                  className="px-6 py-3 text-left text-xs font-medium text-zinc-300 uppercase tracking-wider cursor-pointer hover:bg-zinc-600"
                >
                  <div className="flex items-center">
                    Order Type
                    <SortIcon columnKey="order_type" />
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('order_status')}
                  className="px-6 py-3 text-left text-xs font-medium text-zinc-300 uppercase tracking-wider cursor-pointer hover:bg-zinc-600"
                >
                  <div className="flex items-center">
                    Status
                    <SortIcon columnKey="order_status" />
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('order_datetime')}
                  className="px-6 py-3 text-left text-xs font-medium text-zinc-300 uppercase tracking-wider cursor-pointer hover:bg-zinc-600"
                >
                  <div className="flex items-center">
                    DateTime
                    <SortIcon columnKey="order_datetime" />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedFilteredOrders.length > 0 ? (
                sortedFilteredOrders.map((group, index) => (
                  <React.Fragment key={`${group.symbol}-${group.side}-${index}`}>
                    {/* Main row */}
                    <tr className="hover:bg-zinc-750 bg-zinc-800">
                      <td className="px-6 py-4 text-sm font-medium text-zinc-200">
                        {group.symbol}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border ${getSideTagColor(group.side)}`}>
                          {group.side}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-zinc-300">
                        ₹{formatPrice(group.price)}
                      </td>
                      <td className="px-6 py-4 text-sm text-zinc-300">
                        {group.order_type}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border ${getOrderStatusStyle(group.order_status)}`}>
                          {group.order_status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-zinc-300">
                        {group.order_datetime}
                      </td>
                    </tr>

                    {/* Sub-table row for quantities and clients */}
                    <tr className="border-b-2 border-zinc-600">
                      <td colSpan="6" className="p-0 bg-zinc-850">
                        <div className="px-6 py-3 bg-zinc-900/50">
                          <table className="w-full border border-zinc-700 rounded">
                            <thead className="bg-zinc-700">
                              <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-zinc-300 uppercase w-24">
                                  Qty
                                </th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-zinc-300 uppercase">
                                  Clients
                                </th>
                                {activeTab === 'rejected' && (
                                  <th className="px-4 py-2 text-left text-xs font-medium text-zinc-300 uppercase">
                                    Message
                                  </th>
                                )}
                              </tr>
                            </thead>
                            <tbody className="bg-zinc-800/50">
                              {group.quantities.map((qtyGroup, qtyIndex) => (
                                <tr key={qtyIndex} className="border-t border-zinc-700">
                                  <td className="px-4 py-3 text-sm font-semibold text-zinc-200 align-top">
                                    {qtyGroup.qty}
                                  </td>
                                  <td className="px-4 py-3">
                                    <div className="flex flex-wrap gap-2">
                                      {qtyGroup.clients.map((client, clientIndex) => (
                                        <div 
                                          key={clientIndex} 
                                          className="flex items-center space-x-1 bg-emerald-900/40 text-emerald-400 px-2 py-1 rounded border border-emerald-700 text-xs font-medium"
                                        >
                                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                          </svg>
                                          <span className="whitespace-nowrap">{client.name}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </td>
                                  {activeTab === 'rejected' && (
                                    <td className="px-4 py-3">
                                      <div className="flex flex-wrap gap-2">
                                        {qtyGroup.clients.map((client, clientIndex) => (
                                          client.message && client.message !== 'N/A' && (
                                            <div 
                                              key={clientIndex} 
                                              className="text-xs text-red-400 bg-red-900/30 px-2 py-1 rounded border border-red-700"
                                            >
                                              <span className="font-medium">{client.name}:</span> {client.message}
                                            </div>
                                          )
                                        ))}
                                      </div>
                                    </td>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-zinc-200">
                    No orders found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default OpenOrdersTable;












































// import React, { useState, useMemo } from 'react';
// import { Search, RefreshCw, Loader2 } from 'lucide-react';

// const OpenOrdersTable = ({ orders, isLoading, error, onRefresh ,updating }) => {
//   const [searchTerm, setSearchTerm] = useState("");
//   const [activeTab, setActiveTab] = useState("all");

//   // Helper function to safely format price
//   const formatPrice = (price) => {
//     if (price === null || price === undefined) {
//       return '0.00';
//     }
//     const numPrice = typeof price === 'string' ? parseFloat(price) : price;
//     if (isNaN(numPrice)) {
//       return '0.00';
//     }
//     return numPrice.toFixed(2);
//   };

//   // Helper function to categorize orders by status
//   const categorizeOrders = (orders) => {
//     const openStatuses = ['TRANSIT', 'PENDING', 'OPEN'];
//     const executedStatuses = ['COMPLETE', 'EXECUTED', 'TRADED'];
//     const rejectedStatuses = ['CANCELLED', 'REJECTED'];

//     return {
//       all: orders,
//       open: orders.filter(order => openStatuses.some(status => 
//         order.order_status.toLowerCase().includes(status.toLowerCase())
//       )),
//       executed: orders.filter(order => executedStatuses.some(status => 
//         order.order_status.toLowerCase().includes(status.toLowerCase())
//       )),
//       rejected: orders.filter(order => rejectedStatuses.some(status => 
//         order.order_status.toLowerCase().includes(status.toLowerCase())
//       ))
//     };
//   };

//   // Get current tab's orders
//   const getCurrentTabOrders = () => {
//     const categorizedOrders = categorizeOrders(orders);
//     return categorizedOrders[activeTab] || [];
//   };

//   // Group orders by symbol and side
//   const groupedOrders = useMemo(() => {
//     const currentTabOrders = getCurrentTabOrders();
//     const grouped = new Map();

//     currentTabOrders.forEach((order) => {
//       const key = `${order.symbol}-${order.side}`;

//       if (!grouped.has(key)) {
//         grouped.set(key, {
//           symbol: order.symbol,
//           side: order.side,
//           price: order.price,
//           order_type: order.order_type,
//           order_status: order.order_status,
//           order_datetime: order.order_datetime,
//           quantities: [],
//         });
//       }

//       const group = grouped.get(key);

//       // Find or create quantity group
//       let qtyGroup = group.quantities.find((q) => q.qty === order.qty);
//       if (!qtyGroup) {
//         qtyGroup = {
//           qty: order.qty,
//           clients: [],
//         };
//         group.quantities.push(qtyGroup);
//       }

//       // Add client to quantity group
//       qtyGroup.clients.push({
//         client_id: order.client_id,
//         name: order.name,
//         order_status: order.order_status,
//         message: order.message,
//         order_id: order.order_id,
//       });
//     });

//     // Sort quantities within each group
//     grouped.forEach((group) => {
//       group.quantities.sort((a, b) => a.qty - b.qty);
//     });

//     return Array.from(grouped.values());
//   }, [orders, activeTab]);

//   const getOrderStatusStyle = (status) => {
//     const statusLower = status.toLowerCase();
//     switch (statusLower) {
//       case 'complete':
//       case 'executed':
//       case 'traded':
//         return 'bg-green-900/30 text-green-400 border-green-700';
//       case 'open':
//       case 'active':
//       case 'pending':
//       case 'transit':
//         return 'bg-blue-900/30 text-blue-400 border-blue-700';
//       case 'cancelled':
//       case 'rejected':
//       case 'error':
//         return 'bg-red-900/30 text-red-400 border-red-700';
//       default:
//         return 'bg-zinc-700 text-zinc-300 border-zinc-600';
//     }
//   };

//   const getSideTagColor = (side) => {
//     const sideUpper = side.toUpperCase();
//     if (sideUpper === 'BUY' || sideUpper === 'LONG') {
//       return 'bg-green-900/40 text-green-400 border-green-700';
//     } else if (sideUpper === 'SELL' || sideUpper === 'SHORT') {
//       return 'bg-red-900/40 text-red-400 border-red-700';
//     }
//     return 'bg-zinc-700 text-zinc-300 border-zinc-600';
//   };

//   // Filter grouped orders based on search term
//   const filteredGroupedOrders = useMemo(() => {
//     if (!searchTerm) return groupedOrders;
    
//     return groupedOrders.filter(group => {
//       const searchMatch = 
//         group.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
//         group.quantities.some(qty => 
//           qty.clients.some(client => 
//             client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
//             client.client_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
//             client.order_id.toLowerCase().includes(searchTerm.toLowerCase())
//           )
//         );
//       return searchMatch;
//     });
//   }, [groupedOrders, searchTerm]);

//   // Get order counts for each tab
//   const orderCounts = useMemo(() => {
//     const categorized = categorizeOrders(orders);
//     return {
//       all: categorized.all.length,
//       open: categorized.open.length,
//       executed: categorized.executed.length,
//       rejected: categorized.rejected.length
//     };
//   }, [orders]);

//   if (isLoading) {
//     return (
//       <div className="flex items-center justify-center h-64 bg-zinc-900">
//         <Loader2 className="h-10 w-10 animate-spin text-zinc-400" />
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="flex flex-col items-center justify-center h-64 text-red-400 space-y-4 bg-zinc-900">
//         <p className="text-center max-w-md">{error}</p>
//         <button 
//           onClick={onRefresh} 
//           className="px-4 py-2 bg-zinc-800 text-zinc-200 rounded-md border border-zinc-700 hover:bg-zinc-700"
//         >
//           Retry
//         </button>
//       </div>
//     );
//   }

//   return (
//     <div className="flex flex-col space-y-4 bg-zinc-900 text-zinc-200">
//       {/* Header Controls */}
//       <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 px-4 pt-4">
//         {/* Tabs */}
//         <div className="flex gap-2 bg-zinc-800 p-1 rounded-lg">
//           {['all', 'open', 'executed', 'rejected'].map((tab) => (
//             <button
//               key={tab}
//               onClick={() => setActiveTab(tab)}
//               className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
//                 activeTab === tab 
//                   ? 'bg-zinc-700 text-white' 
//                   : 'text-zinc-400 hover:text-zinc-200'
//               }`}
//             >
//               {tab.charAt(0).toUpperCase() + tab.slice(1)} ({orderCounts[tab]})
//             </button>
//           ))}
//         </div>

//         {/* Search and Refresh */}
//         <div className="flex items-center gap-4 w-full md:w-auto">
//           <div className="relative flex-1 md:w-80">
//             <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500" />
//             <input 
//               placeholder="Search by Symbol or Client Name..."
//               value={searchTerm}
//               onChange={(e) => setSearchTerm(e.target.value)}
//               className="pl-10 w-full px-4 py-2 bg-zinc-800 text-zinc-200 rounded-md border border-zinc-700 focus:border-zinc-600 focus:outline-none"
//             />
//           </div>

//           <button 
//             onClick={onRefresh}
//             className="flex items-center gap-2 px-4 py-2 bg-zinc-800 text-zinc-200 rounded-md border border-zinc-700 hover:bg-zinc-700 transition-colors"
//           >
//             <RefreshCw className="h-4 w-4" />
//             {updating === true ? <Loader2 className="h-4 w-4 animate-spin" /> : <span className="hidden sm:inline">Update</span>}
//           </button>
//         </div>
//       </div>

//       {/* Table */}
//       <div className="bg-zinc-800 border border-zinc-700 h-[calc(100vh-150px)] overflow-y-auto">
//         <div className="">
//           <table className="w-full relative">
//             <thead className="bg-zinc-700 sticky top-0 h-[40px] z-10">
//               <tr>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-zinc-300 uppercase tracking-wider">
//                   Symbol
//                 </th>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-zinc-300 uppercase tracking-wider">
//                   Side
//                 </th>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-zinc-300 uppercase tracking-wider">
//                   Price
//                 </th>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-zinc-300 uppercase tracking-wider">
//                   Order Type
//                 </th>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-zinc-300 uppercase tracking-wider">
//                   Status
//                 </th>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-zinc-300 uppercase tracking-wider">
//                   DateTime
//                 </th>
//               </tr>
//             </thead>
//             <tbody>
//               {filteredGroupedOrders.length > 0 ? (
//                 filteredGroupedOrders.map((group, index) => (
//                   <React.Fragment key={`${group.symbol}-${group.side}-${index}`}>
//                     {/* Main row */}
//                     <tr className="  hover:bg-zinc-750 bg-zinc-800">
//                       <td className="px-6 py-4 text-sm font-medium text-zinc-200">
//                         {group.symbol}
//                       </td>
//                       <td className="px-6 py-4">
//                         <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border ${getSideTagColor(group.side)}`}>
//                           {group.side}
//                         </span>
//                       </td>
//                       <td className="px-6 py-4 text-sm text-zinc-300">
//                         ₹{formatPrice(group.price)}
//                       </td>
//                       <td className="px-6 py-4 text-sm text-zinc-300">
//                         {group.order_type}
//                       </td>
//                       <td className="px-6 py-4">
//                         <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border ${getOrderStatusStyle(group.order_status)}`}>
//                           {group.order_status}
//                         </span>
//                       </td>
//                       <td className="px-6 py-4 text-sm text-zinc-300">
//                         {group.order_datetime}
//                       </td>
//                     </tr>

//                     {/* Sub-table row for quantities and clients */}
//                     <tr className="border-b-2 border-zinc-600">
//                       <td colSpan="6" className="p-0 bg-zinc-850">
//                         <div className="px-6 py-3 bg-zinc-900/50">
//                           <table className="w-full border border-zinc-700 rounded">
//                             <thead className="bg-zinc-700">
//                               <tr>
//                                 <th className="px-4 py-2 text-left text-xs font-medium text-zinc-300 uppercase w-24">
//                                   Qty
//                                 </th>
//                                 <th className="px-4 py-2 text-left text-xs font-medium text-zinc-300 uppercase">
//                                   Clients
//                                 </th>
//                                 {activeTab === 'rejected' && (
//                                   <th className="px-4 py-2 text-left text-xs font-medium text-zinc-300 uppercase">
//                                     Message
//                                   </th>
//                                 )}
//                               </tr>
//                             </thead>
//                             <tbody className="bg-zinc-800/50">
//                               {group.quantities.map((qtyGroup, qtyIndex) => (
//                                 <tr key={qtyIndex} className="border-t border-zinc-700">
//                                   <td className="px-4 py-3 text-sm font-semibold text-zinc-200 align-top">
//                                     {qtyGroup.qty}
//                                   </td>
//                                   <td className="px-4 py-3">
//                                     <div className="flex flex-wrap gap-2">
//                                       {qtyGroup.clients.map((client, clientIndex) => (
//                                         <div 
//                                           key={clientIndex} 
//                                           className="flex items-center space-x-1 bg-emerald-900/40 text-emerald-400 px-2 py-1 rounded border border-emerald-700 text-xs font-medium"
//                                         >
//                                           <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
//                                             <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
//                                           </svg>
//                                           <span className="whitespace-nowrap">{client.name}</span>
//                                         </div>
//                                       ))}
//                                     </div>
//                                   </td>
//                                   {activeTab === 'rejected' && (
//                                     <td className="px-4 py-3">
//                                       <div className="flex flex-wrap gap-2">
//                                         {qtyGroup.clients.map((client, clientIndex) => (
//                                           client.message && client.message !== 'N/A' && (
//                                             <div 
//                                               key={clientIndex} 
//                                               className="text-xs text-red-400 bg-red-900/30 px-2 py-1 rounded border border-red-700"
//                                             >
//                                               <span className="font-medium">{client.name}:</span> {client.message}
//                                             </div>
//                                           )
//                                         ))}
//                                       </div>
//                                     </td>
//                                   )}
//                                 </tr>
//                               ))}
//                             </tbody>
//                           </table>
//                         </div>
//                       </td>
//                     </tr>
//                   </React.Fragment>
//                 ))
//               ) : (
//                 <tr>
//                   <td colSpan="6" className="px-6 py-12 text-center text-zinc-200">
//                     No orders found.
//                   </td>
//                 </tr>
//               )}
//             </tbody>
//           </table>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default OpenOrdersTable;

